import { useTranslation } from "react-i18next";
import {
  addSetInRound,
  removeSetInRound,
  setSetCountInRound,
  type Draft,
  type RoundSetCountResult,
  type RoundSetResult,
} from "@heraion/shared";

import type { ToastAction } from "../primitives/Toast";
import type { ProgramEditorV3Props } from "./types";

type Args = Pick<ProgramEditorV3Props, "draft" | "setDraft" | "showToast"> & { sIdx: number; undo: ToastAction };

type Result = RoundSetResult | RoundSetCountResult;

// The set count of ONE round of one exercise (#336 S1): one more, one less
// (the phone's «Per round» lens, brief §15 row 23) or a number typed in the
// wide grid's «Sets» lens (row 36). The superset follows (Q7). Checked on the
// draft on screen first: refused, the toast says why; done, the toast names
// the round and offers Undo.
export function useRoundSetWrites({ draft, setDraft, showToast, sIdx, undo }: Args) {
  const { t } = useTranslation();

  const inRound = <R extends Result>(run: (d: Draft) => R, n: number, done: (res: Extract<R, { ok: true }>) => string) => {
    const res = run(draft);
    if (!res.ok) {
      return showToast(
        res.reason === "lastSet"
          ? t("editorV3.roundOnlySet", {
              n,
              defaultValue: "W{{n}} has one set left. Take the exercise out of the round instead.",
            })
          : t("editorV3.roundMixedBound", {
              n,
              defaultValue: "W{{n}} unchanged: a timed set and a reps set would swap places.",
            }),
      );
    }
    // Nothing changed (round not active, same count): nothing to announce.
    if (res.draft === draft) return;
    setDraft((d) => {
      const next = run(d);
      return next.ok ? next.draft : d;
    });
    showToast(done(res as Extract<R, { ok: true }>), undo);
  };

  return {
    addSet: (slotIdx: number, week: number) =>
      inRound(
        (d) => addSetInRound(d, sIdx, slotIdx, week),
        week + 1,
        () => t("editorV3.roundSetAdded", { n: week + 1, defaultValue: "W{{n}}: one set more" }),
      ),
    removeSet: (slotIdx: number, week: number, rowIdx: number) =>
      inRound(
        (d) => removeSetInRound(d, sIdx, slotIdx, week, rowIdx),
        week + 1,
        () => t("editorV3.roundSetRemoved", { n: week + 1, defaultValue: "W{{n}}: one set less" }),
      ),
    /** The «Sets» lens: round `week` gets `count` sets, as shared clamps it. */
    setCount: (slotIdx: number, week: number, count: number) =>
      inRound(
        (d) => setSetCountInRound(d, sIdx, slotIdx, week, count),
        week + 1,
        (res) => t("editorV3.roundSetCount", { n: week + 1, count: res.count, defaultValue: "W{{n}}: {{count}} sets" }),
      ),
  };
}
